import Logger from "../../logger/logger.js";
import * as proj from 'ol/proj';
import * as olgeom from 'ol/geom';
import * as ollayer from 'ol/layer';
import * as olextent from 'ol/extent';
import * as olcontrol from 'ol/control';
import { Map, Collection, View, Feature } from 'ol';
import { Select } from 'ol/interaction';
import { Style, Icon } from 'ol/style';
import { Vector, XYZ } from 'ol/source';
import {inAndOut, easeOut} from 'ol/easing';

import 'ol/ol.css';
import { zone_style_fun } from './zone_styles';
import { taxi_style_fun, taxi_style_min_fun, taxi_style_full_fun } from './taxi_styles';
import { nav_point_style_fun } from './nav_point_styles';
import { poi_style_fun } from './MapPOI_styles';
import { route_style_fun } from './route_styles';

const log = new Logger("lib/OSMMap")

export default class OSMMap
{
  constructor(element_id)
  {
    this.element_id = element_id;

    this.map = null;
    this.padding = [48, 48, 48, 48];
    this.taxi_click_callback = null;
    this.taxis_features = {};
    this.taxis_collection = new Collection();
    this.taxi_style = 'default';
    this.client_color = null;

    this.taxis_layer = new ollayer.Vector({
      style: this.get_taxi_style_fun.bind(this),
      source: new Vector({
        features: this.taxis_collection
      })
    });

    this.zones_features = {};
    this.zones_collection = new Collection();
    this.zones_layer = new ollayer.Vector({
      style: zone_style_fun,
      source: new Vector({
        features: this.zones_collection
      })
    });

    this.nav_points_features = {};
    this.nav_points_collection = new Collection();
    this.nav_points_layer = new ollayer.Vector({
      //declutter: true,
      style: nav_point_style_fun,
      source: new Vector({
        features: this.nav_points_collection
      })
    });

    this.pois_features = {};
    this.pois_collection = new Collection();
    this.pois_layer = new ollayer.Vector({
      //declutter: true,
      style: (feature) => poi_style_fun(feature, this.client_color),
      source: new Vector({
        features: this.pois_collection
      })
    });

    this.route_features = {};
    this.route_collection = new Collection();
    this.route_layer = new ollayer.Vector({
      //declutter: true,
      style: route_style_fun,
      source: new Vector({
        features: this.route_collection
      })
    });

    const ATTRIBUTION =
      '&#169; ' +
      '<a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> ' +
      'contributors.';

    var attribution = new olcontrol.Attribution({
      collapsible: false,
      collapsed: false
    });
    this.map = new Map({
      target: this.element_id,
      controls: olcontrol.defaults({ attribution: false }).extend([attribution]),
      layers: [
        new ollayer.Tile({
          source: new XYZ({
            //tilePixelRatio: window.devicePixelRatio,
            maxZoom: 18,
            attributions: ATTRIBUTION,
            url: 'https://tile.system.taxi/{z}/{x}/{y}.png'
          })
        }),
        this.zones_layer,
        this.nav_points_layer,
        this.pois_layer,
        this.taxis_layer,
        this.route_layer,
      ],
      view: new View({
        center: proj.fromLonLat([19.5, 52]),
        zoom: 0,
        padding: this.padding,
        maxZoom: 22
      })
    });
    const select = new Select({
      style: null,
      layers: [this.taxis_layer],
    });
    this.map.addInteraction(select);
    let self = this;
    select.on('select', function (e)
    {
      if (e.selected.length === 0 || !self.taxi_click_callback)
      {
        return;
      }
      log.d("SELECTED", e.selected);
      let features = select.getFeatures();
      features.clear();
      self.taxi_click_callback(e.selected[0].get('taxido'));
    });
  }

  shown()
  {
    this.map.setTarget(this.element_id);
  }

  hidden()
  {
    this.map.setTarget(null);
  }

  set_padding(padding = [48, 48, 48, 48]) {
    this.padding = padding;
  }

  set_taxi_click_callback(callback)
  {
    this.taxi_click_callback = callback;
  }

  fit_taxi(call_number)
  {
    if (this.taxis_features.hasOwnProperty(call_number))
    {
      let feature = this.taxis_features[call_number];
      this.fit([feature]);
    }
  }

  fit_poi(id)
  {
    if (this.pois_features.hasOwnProperty(id))
    {
      const feature = this.pois_features[id];
      this.fit([feature]);
    }
  }

  set_taxi_style(style)
  {
    this.taxi_style = style;
    this.taxis_layer.changed();
  }

  get_taxi_style_fun(feature)
  {
    //log.d("get_taxi_style_fun", this);
    //log.d("get_taxi_style_fun", this.taxi_style);
    if (this.taxi_style === 'min')
    {
      //log.d("get_taxi_style_fun=min");
      return taxi_style_min_fun(feature);
    }
    if (this.taxi_style === 'full')
    {
      //log.d("get_taxi_style_fun=full");
      return taxi_style_full_fun(feature);
    }
    return taxi_style_fun(feature);
  }

  setTaxisVisible(visible)
  {
    this.taxis_layer.setVisible(visible);
  }

  updateTaxi(taxi)
  {
    //log.m("updateTaxi", taxi);
    let taxi_feature = null;
    if (!(this.taxis_features.hasOwnProperty(taxi.call_number)))
    {
      //log.m("updateTaxi create");
      //log.m(taxi);
      taxi_feature = new Feature({
        geometry: new olgeom.Point(proj.fromLonLat([taxi.lng, taxi.lat])),
        name: taxi.call_number,
        taxido: taxi,
        visible: false,
      });
      taxi_feature.setId(taxi.id);
      this.taxis_features[taxi.call_number] = taxi_feature;
      this.taxis_collection.push(taxi_feature);
    }
    else
    {
      //log.m("updateTaxi update");
      taxi_feature = this.taxis_features[taxi.call_number];
      taxi_feature.set('taxido', taxi, true)
      var geom = taxi_feature.getGeometry();
      geom.setCoordinates(proj.fromLonLat([taxi.lng, taxi.lat]));
    }
  };

  setZonesVisible(visible)
  {
    this.zones_layer.setVisible(visible);
  }

  setZones(zones)
  {
    let visible = this.zones_layer.getVisible();
    this.setZonesVisible(false);
    this.zones_collection.clear();

    for (let zone in zones)
    {
      this.setZone(zones[zone]);
    }
    this.setZonesVisible(visible);
  }

  setZone(zonedo)
  {
    //log.d('points', zonedo.points);
    //log.d('zonedo', zonedo);
    if (!zonedo.points || zonedo.disabled)
    {
      return;
    }
    //log.d('GEOM');
    let map_points = [];
    zonedo.points.forEach(value =>
    {
      map_points.push(proj.fromLonLat([value.lng, value.lat]));
    });
    let zone_geom = new olgeom.Polygon([map_points]);
    let feature = new Feature({
      geometry: zone_geom,
      zonedo: zonedo,
      visible: false,
    });
    this.zones_features[zonedo.id] = feature;
    this.zones_collection.push(feature);
    feature.set('visible', true);
  };

  setNavPointsVisible(visible)
  {
    this.nav_points_layer.setVisible(visible);
  }

  setNavPoints(nav_points)
  {
    let visible = this.nav_points_layer.getVisible();
    this.setNavPointsVisible(false);
    this.nav_points_collection.clear();

    for (let nav_point in nav_points)
    {
      this.setNavPoint(nav_points[nav_point]);
    }
    this.setNavPointsVisible(visible);
  }

  setNavPoint(nav_pointdo)
  {
    //log.d('nav_pointdo', nav_pointdo);
    if (!nav_pointdo.lat || !nav_pointdo.lng)
    {
      return;
    }
    let nav_point_geom = new olgeom.Point(
      proj.fromLonLat([nav_pointdo.lng, nav_pointdo.lat]
      ));
    let feature = new Feature({
      geometry: nav_point_geom,
      nav_pointdo: nav_pointdo,
      visible: false,
    });
    this.nav_points_features[nav_pointdo.id] = feature;
    this.nav_points_collection.push(feature);
    feature.set('visible', true);
  };

  setPOIsVisible(visible)
  {
    this.pois_layer.setVisible(visible);
  }

  setPOIs(pois)
  {
    let visible = this.pois_layer.getVisible();
    this.setPOIsVisible(false);
    this.pois_collection.clear();

    for (let poi in pois)
    {
      this.setPOI(pois[poi]);
    }
    this.setPOIsVisible(visible);
    this.pois_layer.setZIndex(100);
    this.pois_layer.changed()
    // this.fit(this.pois_collection);
  }

  setPOI(poido)
  {
    log.d('setPOI: poido', poido);
    if (!poido || !poido.lat || !poido.lng)
    {
      log.d('setPOI: poido not good!', poido);
      return;
    }
    let poi_geom = new olgeom.Point(
      proj.fromLonLat([poido.lng, poido.lat]
      ));
    let feature = new Feature({
      geometry: poi_geom,
      poido: poido,
      visible: true,
    });
    this.pois_features[poido.id] = feature;
    this.pois_collection.push(feature);
  };

  removePOI(id) {
    const feature = this.pois_features[id];
    if (feature) {
      this.pois_collection.remove(feature);
      delete this.pois_features[id];
    }
  };

  setRoute(route)
  {
    if(!route || route.points?.length === 0) {
      return
    }
    let map_points = route.points?.map(p => {
      const { lat, lng } = p;
      return proj.fromLonLat([lng, lat]);
    });
    let line = new olgeom.LineString(map_points);
    let feature = new Feature({
        geometry: line,
        type: route.type || 'default',
        color: route.color || '#000',
        visible: true,
    });
    this.route_collection.push(feature);
  };

  setRoutes(routes)
  {
    this.route_layer.setVisible(false);
    this.clearRoute();
    for (let route in routes)
    {
      if(routes[route].points) {
        this.setRoute(routes[route]);
      }
    }
    this.route_layer.setVisible(true);
    this.route_layer.setZIndex(50);
    // this.fit(this.route_collection, [48, 48, 248, 48]);
  }

  clearRoute()
  {
    this.route_feature = null;
    this.route_collection.clear();
  }

  fitRoutes() {
    this.fit_anim(this.route_collection);
  }

  fitPOIs() {
    this.fit_core(this.pois_collection);
  }

  fit_core(feature_list, animate = false) {
    var extent = olextent.createEmpty();

    feature_list.forEach(function (feature) {
      let geom = feature.getGeometry();
      let geom_extent = geom.getExtent();
      if (feature.get('visible')) {
        olextent.extend(extent, geom_extent);
      }
    });

    extent = olextent.buffer(extent, 100);

    if (olextent.getArea(extent) > 0) {
      const view = this.map.getView();

      if(animate) {
        view.fit(extent, { padding: this.padding, duration: 1000, easing: easeOut });
        return;
      }
      view.fit(extent, { padding: this.padding});
    }
  }

  fit(feature_list) {
    this.fit_core(feature_list, false);
  }

  fit_anim(feature_list) {
    this.fit_core(feature_list, true);
  }

  get_all_collection()
  {
      let all = this.taxis_collection.getArray()
          .concat(this.pois_collection.getArray())
          .concat(this.route_collection.getArray())
      if (this.zones_layer.getVisible()) {
          all = all.concat(this.zones_collection.getArray());
      }
      return all
  }

  fit_all() {
    this.fit(this.get_all_collection());
  }

  fit_all_anim() {
    this.fit_anim(this.get_all_collection());
  }

  set_center(lat, lng, zoom = null)
  {
    const view = this.map.getView();
    view.setCenter(proj.fromLonLat([lng, lat]));
    if (zoom) {
      view.setZoom(zoom);
    }
  }

  update_size()
  {
    this.map.updateSize();
  }

  render()
  {
      return this.map.render()
  }

  renderSync()
  {
      return this.map.renderSync()
  }

  set_client_color(color)
  {
    this.client_color = color;
  }
}
